import React, { useState, useEffect } from 'react';
import { Download, FileText, AlertCircle, CheckCircle } from 'lucide-react';
import { getDocumentInfo, downloadDocument } from '../lib/documents';

interface DocumentDownloadProps {
  productId: string;
  className?: string;
}

const DocumentDownload: React.FC<DocumentDownloadProps> = ({ productId, className = '' }) => {
  const [documentInfo, setDocumentInfo] = useState<{
    available: boolean;
    loading: boolean;
    error: string | null;
  }>({
    available: false,
    loading: true,
    error: null
  });

  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    checkDocument();
  }, [productId]);

  const checkDocument = async () => {
    try {
      setDocumentInfo(prev => ({ ...prev, loading: true, error: null }));
      const info = await getDocumentInfo(productId);
      setDocumentInfo({
        available: info.available,
        loading: false,
        error: null
      });
    } catch (error) {
      setDocumentInfo({
        available: false,
        loading: false,
        error: 'Ошибка при проверке документа'
      });
    }
  };

  const handleDownload = async () => {
    if (!documentInfo.available || downloading) return;

    try {
      setDownloading(true);
      await downloadDocument(productId);
    } catch (error) {
      console.error('Download failed:', error);
      // Показываем уведомление об ошибке
      alert('Ошибка при скачивании документа. Попробуйте позже.');
    } finally {
      setDownloading(false);
    }
  };

  if (documentInfo.loading) {
    return (
      <div className={`animate-pulse ${className}`}>
        <div className="h-12 bg-gray-200 dark:bg-gray-700 rounded-lg"></div>
      </div>
    );
  }

  if (documentInfo.error) {
    return (
      <div className={`${className}`}>
        <div className="flex items-center justify-center p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
          <AlertCircle className="w-5 h-5 text-red-500 mr-2" />
          <span className="text-sm text-red-600 dark:text-red-400">
            {documentInfo.error}
          </span>
        </div>
      </div>
    );
  }

  if (!documentInfo.available) {
    return (
      <div className={`${className}`}>
        <div className="flex items-center justify-center p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
          <AlertCircle className="w-5 h-5 text-amber-500 mr-2" />
          <span className="text-sm text-amber-600 dark:text-amber-400">
            К сожалению, документ пока не добавлен, попробуйте скачать позже
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className={`${className}`}>
      <button
        onClick={handleDownload}
        disabled={downloading}
        className="inline-flex items-center space-x-2 text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 font-medium transition-colors duration-200 cursor-pointer"
      >
        {downloading ? (
          <>
            <div className="w-4 h-4 border-2 border-primary-600 border-t-transparent rounded-full animate-spin"></div>
            <span>Скачивание...</span>
          </>
        ) : (
          <>
            <Download className="w-4 h-4" />
            <span>Скачать рекомендации по применению</span>
          </>
        )}
      </button>
    </div>
  );
};

export default DocumentDownload;
